// @ts-check
// Configurações de usuário e da instituição na SPA (docs/arquitetura-appcrm/08-contrato-configuracoes.md): perfil,
// experiência, geral, segurança e troca de instituição.
import { api } from './api-client.js';

/**
 * @typedef {{ id: string, slug: string, displayName: string, logoUrl: string | null, brandLogoUrl?: string | null,
 *   brandColors?: { primary: string, secondary: string, accent: string } | null }} Workspace
 * @typedef {{ id: string, domain: string, validationEmail: string, isValidated: boolean, validatedAt: string | null, createdAt: string }} ApprovedDomain
 * @typedef {{ url: string, createdAt: string }} PublicInvite
 * @typedef {{ slug: string, displayName: string, logoUrl: string | null, isCurrent: boolean }} Institution
 */

/**
 * Perfil e preferências do próprio membro.
 * @param {Record<string, unknown>} patch
 * @returns {Promise<Record<string, any>>} membro atualizado
 */
export async function updateOwnProfile(patch) {
  const body = await api.patch('/rest/workspace/profile', patch);
  return body?.data?.workspaceMember ?? {};
}

/**
 * Envia uma imagem já recortada e devolve o caminho de download (o mesmo que a API grava no perfil ou no logo).
 * @param {File} file
 * @returns {Promise<{ id: string, url: string, previewUrl: string }>}
 */
export async function uploadImage(file) {
  const formData = new FormData();
  formData.append('file', file, file.name);
  const body = await api.upload('/rest/files', formData);
  const uploaded = body?.data?.file ?? {};
  return { id: uploaded.id, url: `/rest/files/${uploaded.id}`, previewUrl: uploaded.url ?? `/rest/files/${uploaded.id}` };
}

/** Arquivo enviado e não usado (foto trocada) sai do armazenamento. @param {string} fileUrl */
export async function releaseUploadedImage(fileUrl) {
  const match = /^\/rest\/files\/([\w-]+)$/.exec(fileUrl);
  if (!match) return;
  await api.delete(`/rest/files/${match[1]}`).catch(() => {});
}

/** @returns {Promise<{ workspace: Workspace, canEdit: boolean }>} */
export async function fetchWorkspace() {
  const body = await api.get('/rest/workspace');
  return body?.data;
}

/**
 * @param {{ displayName?: string, logoFileUrl?: string | null, brandLogoFileUrl?: string | null,
 *   brandColors?: { primary: string, secondary: string, accent: string } | null }} patch
 * @returns {Promise<Workspace>}
 */
export async function updateWorkspace(patch) {
  const body = await api.patch('/rest/workspace', patch);
  return body?.data?.workspace;
}

/** @returns {Promise<{ publicInvite: PublicInvite | null, approvedDomains: ApprovedDomain[] }>} */
export async function fetchSecuritySettings() {
  const body = await api.get('/rest/workspace/security');
  return body?.data ?? { publicInvite: null, approvedDomains: [] };
}

/** @returns {Promise<PublicInvite>} */
export async function generatePublicInvite() {
  const body = await api.post('/rest/workspace/public-invite');
  return body?.data?.publicInvite;
}

export function revokePublicInvite() {
  return api.delete('/rest/workspace/public-invite');
}

/**
 * @param {{ domain: string, email?: string }} input
 * @returns {Promise<{ approvedDomain: ApprovedDomain, emailSent: boolean, devValidationUrl?: string }>}
 */
export async function addApprovedDomain(input) {
  const body = await api.post('/rest/workspace/approved-domains', input);
  return body?.data;
}

/** @param {string} approvedDomainId */
export function removeApprovedDomain(approvedDomainId) {
  return api.delete(`/rest/workspace/approved-domains/${approvedDomainId}`);
}

/** @returns {Promise<Institution[]>} */
export async function listMyInstitutions() {
  const body = await api.get('/auth/institutions');
  return body?.data?.institutions ?? [];
}

/** @param {string} slug @returns {Promise<string>} endereço do handoff na outra instituição */
export async function switchInstitution(slug) {
  const body = await api.post('/auth/switch-institution', { slug });
  return body?.data?.redirectUrl;
}
